import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from '../hooks/use-toast';
import { ArrowLeft, Plus, X, Utensils, Flame, Droplets, ChefHat, Euro } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import api from '../api/axios';

const SaucesConfigPage = () => {
  const navigate = useNavigate();
  const [config, setConfig] = useState({
    sauces: [], accompagnements: [], cuissons: [],
    cuissons_par_categorie: {}, options_par_categorie: {},
    included_accompaniments: 2, extra_accompaniment_fee: 5,
  });
  const [newItem, setNewItem] = useState({ sauces: '', accompagnements: '', cuissons: '' });
  const [newAccompPrix, setNewAccompPrix] = useState('');
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState([]);
  const [newCatCuisson, setNewCatCuisson] = useState({});

  useEffect(() => {
    fetchConfig();
    fetchCategories();
  }, []);

  const fetchConfig = async () => {
    try {
      const { data } = await api.get('/menu/sauces-config');
      if (!data.cuissons_par_categorie) data.cuissons_par_categorie = {};
      if (!data.options_par_categorie) data.options_par_categorie = {};
      if (data.included_accompaniments == null) data.included_accompaniments = 2;
      if (data.extra_accompaniment_fee == null) data.extra_accompaniment_fee = 5;
      setConfig(data);
    } catch (e) { toast({ title: 'Erreur chargement', variant: 'destructive' }); }
  };

  const fetchCategories = async () => {
    try {
      const { data } = await api.get('/menu/categories');
      setCategories(data);
    } catch {}
  };

  const addItem = (type) => {
    const val = newItem[type].trim();
    if (!val) return;
    if (type === 'accompagnements') {
      const exists = config.accompagnements.some(a => (typeof a === 'object' ? a.name : a) === val);
      if (exists) { toast({ title: 'Deja dans la liste', variant: 'destructive' }); return; }
      const prix = parseFloat(newAccompPrix) || 0;
      setConfig({ ...config, accompagnements: [...config.accompagnements, { name: val, prix }] });
      setNewAccompPrix('');
    } else {
      if (config[type].includes(val)) { toast({ title: 'Deja dans la liste', variant: 'destructive' }); return; }
      setConfig({ ...config, [type]: [...config[type], val] });
    }
    setNewItem({ ...newItem, [type]: '' });
  };

  const removeItem = (type, index) => {
    setConfig({ ...config, [type]: config[type].filter((_, i) => i !== index) });
  };

  const addCatCuisson = (catName) => {
    const val = (newCatCuisson[catName] || '').trim();
    if (!val) return;
    const existing = config.cuissons_par_categorie[catName] || [];
    if (existing.includes(val)) { toast({ title: 'Deja dans la liste', variant: 'destructive' }); return; }
    setConfig({
      ...config,
      cuissons_par_categorie: {
        ...config.cuissons_par_categorie,
        [catName]: [...existing, val]
      }
    });
    setNewCatCuisson({ ...newCatCuisson, [catName]: '' });
  };

  const removeCatCuisson = (catName, index) => {
    const list = (config.cuissons_par_categorie[catName] || []).filter((_, i) => i !== index);
    setConfig({
      ...config,
      cuissons_par_categorie: {
        ...config.cuissons_par_categorie,
        [catName]: list
      }
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/menu/sauces-config', config);
      toast({ title: 'Configuration sauvegardee' });
    } catch (e) { toast({ title: 'Erreur', variant: 'destructive' }); }
    finally { setSaving(false); }
  };

  const toggleCatOption = (catName, option) => {
    const current = config.options_par_categorie[catName] || {};
    const val = current[option];
    const newVal = val === undefined ? true : val === true ? false : undefined;
    const newOpts = { ...current };
    if (newVal === undefined) delete newOpts[option];
    else newOpts[option] = newVal;
    setConfig({
      ...config,
      options_par_categorie: { ...config.options_par_categorie, [catName]: newOpts }
    });
  };

  const globalSections = [
    { key: 'sauces', title: 'Sauces', icon: Droplets, color: 'bg-amber-50 border-amber-200', tagColor: 'bg-amber-100 text-amber-800' },
    { key: 'cuissons', title: 'Cuissons (defaut)', icon: Flame, color: 'bg-red-50 border-red-200', tagColor: 'bg-red-100 text-red-800' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#f5f1ed] to-white">
      <div className="bg-[#3d2817] text-white py-3 shadow-lg">
        <div className="max-w-4xl mx-auto px-4 flex items-center gap-4">
          <Button variant="ghost" className="text-white hover:bg-white/10" onClick={() => navigate('/staff/dashboard')} data-testid="back-btn">
            <ArrowLeft size={20} />
          </Button>
          <Utensils size={22} />
          <h1 className="text-lg font-bold">Options de Commande</h1>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        <p className="text-sm text-gray-600">
          Configurez les sauces, accompagnements et modes de cuisson. Les cuissons par categorie sont prioritaires sur les cuissons par defaut.
        </p>

        {/* Global sections: sauces, accompagnements, cuissons default */}
        {globalSections.map(({ key, title, icon: Icon, color, tagColor }) => (
          <Card key={key} className={`border ${color}`} data-testid={`section-${key}`}>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Icon size={18} /> {title} ({config[key]?.length || 0})
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex flex-wrap gap-2">
                {(config[key] || []).map((item, i) => (
                  <Badge key={i} className={`${tagColor} px-3 py-1.5 text-sm cursor-pointer hover:opacity-70`}
                    onClick={() => removeItem(key, i)} data-testid={`tag-${key}-${i}`}>
                    {item} <X size={14} className="ml-1.5" />
                  </Badge>
                ))}
                {(!config[key] || config[key].length === 0) && <span className="text-sm text-gray-400 italic">Aucun element</span>}
              </div>
              <div className="flex gap-2">
                <Input
                  value={newItem[key] || ''}
                  onChange={e => setNewItem({ ...newItem, [key]: e.target.value })}
                  onKeyDown={e => e.key === 'Enter' && addItem(key)}
                  placeholder={`Ajouter ${title.toLowerCase()}...`}
                  className="flex-1" data-testid={`input-${key}`}
                />
                <Button onClick={() => addItem(key)} variant="outline" size="sm" data-testid={`add-${key}-btn`}>
                  <Plus size={16} />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}

        {/* Accompagnements avec prix */}
        <Card className="border bg-green-50 border-green-200" data-testid="section-accompagnements">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Utensils size={18} /> Accompagnements ({(config.accompagnements || []).length})
            </CardTitle>
            <p className="text-xs text-gray-500 mt-1">Les accompagnements avec un prix seront factures en supplement.</p>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex flex-wrap gap-2">
              {(config.accompagnements || []).map((a, i) => {
                const name = typeof a === 'object' ? a.name : a;
                const prix = typeof a === 'object' ? a.prix : 0;
                return (
                  <Badge key={i} className="bg-green-100 text-green-800 px-3 py-1.5 text-sm cursor-pointer hover:opacity-70"
                    onClick={() => removeItem('accompagnements', i)} data-testid={`tag-accomp-${i}`}>
                    {name}{prix > 0 && <span className="ml-1 text-green-600 font-bold">+{prix.toFixed(2)}&euro;</span>} <X size={14} className="ml-1.5" />
                  </Badge>
                );
              })}
              {(!config.accompagnements || config.accompagnements.length === 0) && <span className="text-sm text-gray-400 italic">Aucun element</span>}
            </div>
            <div className="flex gap-2">
              <Input value={newItem.accompagnements} onChange={e => setNewItem({ ...newItem, accompagnements: e.target.value })} onKeyDown={e => e.key === 'Enter' && addItem('accompagnements')} placeholder="Nom accompagnement..." className="flex-1" data-testid="input-accomp-name" />
              <div className="relative w-24">
                <Euro size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
                <Input type="number" step="0.50" value={newAccompPrix} onChange={e => setNewAccompPrix(e.target.value)} placeholder="0.00" className="pl-6 text-sm" data-testid="input-accomp-prix" />
              </div>
              <Button onClick={() => addItem('accompagnements')} variant="outline" size="sm" data-testid="add-accomp-btn">
                <Plus size={16} />
              </Button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 border-t border-green-200 pt-3">
              <div>
                <label className="text-xs font-semibold text-gray-700 block mb-1">Nombre d'accompagnements inclus</label>
                <Input type="number" min="0" max="10" value={config.included_accompaniments}
                  onChange={e => setConfig({ ...config, included_accompaniments: Math.max(0, parseInt(e.target.value) || 0) })}
                  data-testid="included-accompaniments" />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-700 block mb-1">Prix par accompagnement supplémentaire</label>
                <div className="relative">
                  <Euro size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <Input type="number" min="0" max="100" step="0.50" value={config.extra_accompaniment_fee}
                    onChange={e => setConfig({ ...config, extra_accompaniment_fee: Math.max(0, parseFloat(e.target.value) || 0) })}
                    className="pl-8" data-testid="extra-accompaniment-fee" />
                </div>
              </div>
            </div>
            <p className="text-xs font-semibold text-green-800">
              Règle actuelle : {config.included_accompaniments} accompagnement(s) inclus, puis +{Number(config.extra_accompaniment_fee || 0).toFixed(2)} € pour chaque accompagnement supplémentaire.
            </p>
          </CardContent>
        </Card>

        {/* Cuissons par categorie */}
        <Card className="border bg-orange-50 border-orange-200" data-testid="section-cuissons-categories">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <ChefHat size={18} /> Cuissons par Categorie
            </CardTitle>
            <p className="text-xs text-gray-500 mt-1">
              Definissez les modes de cuisson specifiques pour chaque categorie de plat. Si une categorie a des cuissons, elles remplacent les cuissons par defaut.
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            {categories.map(cat => {
              const catName = cat.name;
              const catCuissons = config.cuissons_par_categorie[catName] || [];
              return (
                <div key={catName} className="bg-white rounded-lg border border-orange-100 p-3" data-testid={`cat-cuisson-${catName}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <Flame size={14} className="text-orange-500" />
                    <span className="text-sm font-bold text-gray-700">{catName}</span>
                    {catCuissons.length > 0 && (
                      <Badge className="bg-orange-100 text-orange-700 text-[10px]">{catCuissons.length} option{catCuissons.length > 1 ? 's' : ''}</Badge>
                    )}
                  </div>
                  {/* Category default toggles */}
                  <div className="flex flex-wrap gap-2 mb-2">
                    {[
                      { key: 'sauce', label: 'Sauce', icon: Droplets, activeColor: 'bg-amber-100 text-amber-700 border-amber-300', offColor: 'bg-red-100 text-red-600 border-red-300' },
                      { key: 'accompagnement', label: 'Accomp.', icon: Utensils, activeColor: 'bg-green-100 text-green-700 border-green-300', offColor: 'bg-red-100 text-red-600 border-red-300' },
                      { key: 'cuisson', label: 'Cuisson', icon: Flame, activeColor: 'bg-orange-100 text-orange-700 border-orange-300', offColor: 'bg-red-100 text-red-600 border-red-300' },
                    ].map(({ key, label, icon: Ic, activeColor, offColor }) => {
                      const val = (config.options_par_categorie[catName] || {})[key];
                      return (
                        <button key={key} type="button" onClick={() => toggleCatOption(catName, key)}
                          className={`flex items-center gap-1 text-[10px] px-2 py-1 rounded-full font-bold border transition-colors ${
                            val === undefined ? 'bg-gray-100 text-gray-400 border-gray-200' : val ? activeColor : offColor
                          }`} data-testid={`cat-toggle-${catName}-${key}`}>
                          <Ic size={10} /> {label}: {val === undefined ? 'Auto' : val ? 'OUI' : 'NON'}
                        </button>
                      );
                    })}
                  </div>
                  <div className="flex flex-wrap gap-1.5 mb-2">
                    {catCuissons.map((c, i) => (
                      <Badge key={i} className="bg-orange-100 text-orange-800 px-2.5 py-1 text-xs cursor-pointer hover:opacity-70"
                        onClick={() => removeCatCuisson(catName, i)} data-testid={`cat-tag-${catName}-${i}`}>
                        {c} <X size={12} className="ml-1" />
                      </Badge>
                    ))}
                    {catCuissons.length === 0 && <span className="text-xs text-gray-400 italic">Cuissons par defaut</span>}
                  </div>
                  <div className="flex gap-2">
                    <Input
                      value={newCatCuisson[catName] || ''}
                      onChange={e => setNewCatCuisson({ ...newCatCuisson, [catName]: e.target.value })}
                      onKeyDown={e => e.key === 'Enter' && addCatCuisson(catName)}
                      placeholder="Ex: Frit, Poche, Grille..."
                      className="flex-1 h-8 text-xs" data-testid={`input-cat-${catName}`}
                    />
                    <Button onClick={() => addCatCuisson(catName)} variant="outline" size="sm" className="h-8" data-testid={`add-cat-${catName}-btn`}>
                      <Plus size={14} />
                    </Button>
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>

        <Button onClick={handleSave} disabled={saving} className="w-full bg-[#8B0000] hover:bg-[#6d0000] text-white py-6 text-lg font-bold" data-testid="save-config-btn">
          {saving ? 'Sauvegarde...' : 'Sauvegarder la Configuration'}
        </Button>
      </div>
    </div>
  );
};

export default SaucesConfigPage;
